<template>
    <!--  THE CALCULATION IS TO COMPENSATE FOR SUBTITLES-->
    <!--  PARENT HEIGHT IS NEEDED BECAUSE CHILDREN RENDER FIRST-->
    <div :style="{'height': showSubtitles ? 'calc(100% - 150px)' : '100%'}"
         style="min-height:100px; width:100%; min-width: 100px;" class="audio-wrapper">

        <audio crossorigin="anonymous" :src="url" ref="audio"
               @canplay="audioLoaded"
               @play="onPlay"
               @pause="onPause"
               @ended="onPlayEnd"
               @error="onPlayError">
        </audio>

        <!--    THE WAVE ADJUSTING TO SOUND-->
        <transition name="slide-x-transition">
            <div v-show="mediaReady" ref="audioMotion" class="fill-parent audio" style="max-width: 500px"
                 @click="togglePlayPauseInternally()">
            </div>
        </transition>

        <!--    A CHIP INDICATING PAUSED STATE-->
        <v-chip small class="center elevation-5" style="" v-if="!showOverlay && paused"
                @click="togglePlayPauseInternally()">
            {{ $t('general.audio_paused') }}
        </v-chip>

        <!--    A PAUSED SIRI IMAGE-->
        <div @click="togglePlayPauseInternally()" class="center idle-siriwave blur-on"
             v-show="showOverlay && mediaReady"></div>

        <!--    THE PLAY BUTTON ON THE OVERLAY-->
        <v-btn
                @click="togglePlayPauseInternally()"
                color="white"
                icon
                class="center"
                v-if="showOverlay && mediaReady"
        >
            <v-icon size="60">
                mdi-play-circle-outline
            </v-icon>
        </v-btn>
    </div>

</template>

<script>
    import AudioMotionAnalyzer from 'audiomotion-analyzer';
    export default {
        name: "MediaCompositionAudioMotion",
        props: {
            id: {
                required: true,
            },
            url: {
                type: String,
                required: true,
            },
            volume: {
                type: Number,
                required: true,
            },
            autoplay: {
                default: true,
                type: Boolean
            },
            onProgress: {
                type: Function
            },
            parentHeight: {
                type: String,
                required: true
            },
            showSubtitles: {
                type: Boolean
            },
        },
        watch: {
            'volume': function () {
                const audio = this.$refs.audio;
                if (audio) {
                    audio.volume = this.adjustedVolume;
                }
            },
        },
        computed: {
            adjustedVolume() {
                return this.volume * 0.5;
            }
        },
        data() {
            return {
                progressInterval: null,
                paused: false,
                mediaReady: false,
                showOverlay: false,
                audioMotion: null
            }
        },
        mounted() {
            this.$refs.audio.loop=false;
            this.$refs.audio.volume = this.adjustedVolume;
            this.initAudioMotion();
        },
        methods: {
            initAudioMotion(){

                const outrunGradient ={
                    name: "Outrun",
                            bgColor: "#101",
                            colorStops: [{
                        pos: 0,
                        color: "rgb( 255, 223, 67 )"
                    }, {
                        pos: .182,
                        color: "rgb( 250, 84, 118 )"
                    }, {
                        pos: .364,
                        color: "rgb( 198, 59, 243 )"
                    }, {
                        pos: .525,
                        color: "rgb( 133, 80, 255 )"
                    }, {
                        pos: .688,
                        color: "rgb( 74, 104, 247 )"
                    }, {
                        pos: 1,
                        color: "rgb( 35, 210, 255 )"
                    }],
                            disabled: !1
                }

                this.audioMotion = new AudioMotionAnalyzer(this.$refs.audioMotion,{
                    source: this.$refs.audio,
                    mode: 5,
                    gradient: 'rainbow',
                    bgAlpha: 0.5,
                    frequencyScale: "log",
                    //linearAmplitude: true,
                    linearBoost: true,
                    mirror: 0,
                    lineWidth: 2,
                    channelLayout: 'single',
                    overlay: true,
                    showScaleX:false,
                    showScaleY:false,
                    showBgColor:false,
                    reflexRatio: 0.5,
                    reflexBright: 1,
                    reflexAlpha: 1,
                    roundBars: true,
                    colorMode: 'bar-index',
                    barSpace: 3,
                    showPeaks: false,
                    alphaBars: true,
                });

                this.audioMotion.registerGradient('Outrun',outrunGradient)
                this.audioMotion.setOptions({
                    gradient: 'Outrun'
                })

                this.progressInterval = setInterval(() => {
                    const currentTime = this.$refs.audio.currentTime;
                    if (this.onProgress){
                        this.onProgress(currentTime);
                    }
                }, 800);


            },
            audioLoaded(){
                if (this.mediaReady === true) return;
                this.mediaReady = true;
                this.$emit('mediaReady',this.$refs.audio.duration);
                if (!this.autoplay || (this.audioMotion && this.audioMotion.audioCtx && this.audioMotion.audioCtx.state==="suspended")) {
                    this.showOverlay = true;
                } else {
                    this.$emit('showControls');
                    this.play();
                }
            },
            togglePlayPauseInternally() {
                if (this.showOverlay) {
                    this.showOverlay = false;
                    this.$emit('showControls');
                    this.$emit('play');

                } else {
                    this.paused ? this.$emit('play') : this.$emit('pause');
                }
            },
            play() {
                const audio = this.$refs.audio;

                if (audio.paused) {
                    this.audioMotion.toggleAnalyzer(true);
                    audio.play();
                    this.showOverlay = false;
                    this.paused = false;
                }

            },
            pause() {
                this.audioMotion.toggleAnalyzer(false);
                const audio = this.$refs.audio;
                audio.pause();
                this.paused = true;
            },
            stop() {
                const audio = this.$refs.audio;
                if (audio) {
                    audio.pause();
                    audio.currentTime = 0; // Reset to the beginning
                }
            },
            forward(time = 5) {
                const audio = this.$refs.audio;
                if (audio) {
                    const newTime = Math.min(audio.currentTime + time, audio.duration);
                    audio.currentTime = newTime;
                }
                if (this.onProgress) {
                    this.onProgress(audio.currentTime);
                }
            },
            backward(time = 5) {
                const audio = this.$refs.audio;
                if (audio) {
                    const newTime = Math.max(audio.currentTime - time, 0);
                    audio.currentTime = newTime;
                }
                if (this.onProgress) {
                    this.onProgress(audio.currentTime);
                }
            },
            reset() {
                const audio = this.$refs.audio;
                if (audio) {
                    audio.currentTime = 0;
                    if (this.onProgress) {
                        this.onProgress(audio.currentTime);
                    }
                }
            },
            onPlay() {
                this.$emit('play');
            },
            onPause() {
                this.$emit('paused');
            },
            onPlayError() {
                console.log("An error occurred while trying to play the audio.");
                this.$emit('playError');
            },
            onPlayEnd() {
                this.pause();
                this.$emit('complete', this.id);
                setTimeout(() => {
                    this.showOverlay = true;
                }, 500);
            }
        },
        beforeDestroy() {
            if(this.$refs.audio){
                this.$refs.audio.pause();
            }
            this.$refs.audio.src = null;
            clearInterval(this.progressInterval);
        }
    }
</script>
<style scoped>
    .blur-on, .blur-element {
        -webkit-filter: blur(3px);
        -moz-filter: blur(3px);
        -o-filter: blur(3px);
        -ms-filter: blur(3px);
        filter: blur(3px);
    }

    .fill-parent {
        width: 100%;
        height: 100%;
        /*max-width: 600px;*/
        /*max-height: 200px;*/
    }

    .audio-wrapper {
        position: relative;
        height: 100%;
        display: flex;
        align-content: center;
        justify-content: center;
    }

    .center {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    .idle-siriwave {
        background-image: url("/img/media/idle-siriwave.png");
        background-size: contain;
        width: 100%;
        /*max-width: 600px;*/
        height: 100%;
        /*max-height: 100px;*/
        background-position: center;
        background-repeat: no-repeat;
        /*margin-top:20px;*/
    }
</style>
